/**
 * Client Login Page
 * Separate from Admin OAuth — uses email + password.
 * AYU Sacred Modern Design: warm ivory background, gold accents, high contrast.
 * Supports: Login, Register, Forgot Password, Reset Password (via token in URL)
 * Includes: "Was dich nach der Anmeldung erwartet" section for new users.
 */
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2, ArrowLeft, MessageCircle, Mic, BookOpen, Users, ClipboardList, LayoutDashboard, Sparkles, Shield } from "lucide-react";
import { Link } from "wouter";
import { useI18n } from "@/contexts/I18nContext";
import { IMAGES } from "@/lib/constants";

type Mode = "login" | "register" | "forgot" | "reset";

export default function ClientLogin() {
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [, navigate] = useLocation();
  const { login, register, error: authError } = useClientAuth();

  // Password reset state
  const [newPassword, setNewPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const requestResetMutation = trpc.clientAuth.requestPasswordReset.useMutation();
  const resetPasswordMutation = trpc.clientAuth.resetPassword.useMutation();

  // Check for reset token and redirect param in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset");
    if (token) {
      setResetToken(token);
      setMode("reset");
    }
  }, []);

  const getRedirectTarget = (onboardingComplete: boolean) => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect");
    if (redirect) return redirect;
    return onboardingComplete ? "/app" : "/app/onboarding";
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);
    const result = await login(email, password);
    if (result.success) {
      window.location.href = getRedirectTarget(!!result.onboardingComplete);
      return;
    }
    setIsSubmitting(false);
    setMessage({ type: "error", text: authError || t("auth.ungueltig") });
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setMessage({ type: "error", text: t("auth.passwortZuKurz") });
      return;
    }
    setIsSubmitting(true);
    setMessage(null);
    const result = await register({ email, password, firstName, lastName });
    if (result.success) {
      window.location.href = "/app/onboarding";
      return;
    }
    setIsSubmitting(false);
    setMessage({ type: "error", text: t("auth.registrierungFehler") });
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);
    try {
      await requestResetMutation.mutateAsync({ email, origin: window.location.origin });
    } catch {
      // Silently handle — don't reveal if email exists
    }
    setIsSubmitting(false);
    setMessage({ type: "success", text: t("auth.resetLinkGesendet") });
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setMessage({ type: "error", text: t("auth.passwortZuKurz") });
      return;
    }
    setIsSubmitting(true);
    setMessage(null);
    try {
      const result = await resetPasswordMutation.mutateAsync({ token: resetToken, newPassword });
      if (result.success) {
        setMessage({ type: "success", text: t("auth.passwortGeaendert") });
        setMode("login");
        setResetToken("");
        setNewPassword("");
        window.history.replaceState({}, "", window.location.pathname);
      } else {
        setMessage({ type: "error", text: result.error || t("auth.linkUngueltig") });
      }
    } catch (err: any) {
      setMessage({ type: "error", text: err.message || t("errors.allgemein") });
    }
    setIsSubmitting(false);
  };

  const showOnboardingInfo = mode === "login" || mode === "register";

  return (
    <div className="min-h-screen px-4 py-8 md:py-12 relative"
      style={{ background: "linear-gradient(135deg, #f5f0e8 0%, #ede6d8 50%, #f5f0e8 100%)" }}>
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c9a96e' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />

      {/* Back to Homepage */}
      <Link href="/" className="absolute top-6 left-6 text-[#5a7247] hover:text-[#3d5030] flex items-center gap-2 transition-colors z-10">
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">{t("common.back")}</span>
      </Link>

      <div className="max-w-md mx-auto relative z-10 mt-8">

        {/* ═══════ NEW USER BANNER (shown in login mode) ═══════ */}
        {mode === "login" && (
          <div className="mb-5 bg-[#6b7f4e]/10 border border-[#6b7f4e]/25 rounded-xl p-4 text-center">
            <p className="text-sm font-semibold text-[#2d3a1e] mb-1">
              {t("auth.neuBeiAyu")}
            </p>
            <p className="text-xs text-[#5a5a4a] mb-3">
              {t("auth.kontoErstellenHint")}
            </p>
            <Button
              onClick={() => { setMode("register"); setMessage(null); }}
              className="bg-[#6b7f4e] hover:bg-[#5a6e3f] text-white font-semibold h-10 px-6 text-sm shadow-md"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {t("auth.jetzt")}
            </Button>
          </div>
        )}

        {/* ═══════ FORM CARD ═══════ */}
        <div className="bg-white rounded-2xl shadow-xl border border-[#6b7f4e]/20 overflow-hidden">
          {/* Gold accent bar */}
          <div className="h-1.5 bg-gradient-to-r from-[#6b7f4e] via-[#d4b87a] to-[#5a7247]" />
          
          {/* Header */}
          <div className="text-center pt-8 pb-4 px-8">
            <div className="mx-auto mb-4">
              <Link href="/">
                <img src={IMAGES.logo} alt="AYU" className="h-12 w-auto mx-auto cursor-pointer" />
              </Link>
            </div>
            <h1 className="text-xl font-semibold text-[#2d3a1e]">
              {mode === "login" && t("auth.willkommenZurueck")}
              {mode === "register" && t("auth.kontoErstellen")}
              {mode === "forgot" && t("auth.passwortZuruecksetzen")}
              {mode === "reset" && t("auth.neuesPasswort")}
            </h1>
            <p className="text-sm text-[#7d7060] mt-1">
              {mode === "login" && t("auth.loginSubtitle")}
              {mode === "register" && t("auth.registerSubtitle")}
              {mode === "forgot" && t("auth.forgotSubtitle")}
              {mode === "reset" && t("auth.resetSubtitle")}
            </p>
          </div>

          {/* Content */}
          <div className="px-8 pb-8">
            {message && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                message.type === "error" 
                  ? "bg-red-50 border border-red-200 text-red-700" 
                  : "bg-green-50 border border-green-200 text-green-700"
              }`}>
                {message.text}
              </div>
            )}

            {/* Login Form */}
            {mode === "login" && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2d3a1e] font-medium text-sm">{t("auth.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.platzhalterEmail")}
                    required
                    className="bg-[#f9f6f0] border-[#d4c5a0] text-[#2d3a1e] placeholder:text-[#b5a890] focus:border-[#6b7f4e] focus:ring-[#6b7f4e]/20 h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#2d3a1e] font-medium text-sm">{t("auth.passwort")}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="bg-[#f9f6f0] border-[#d4c5a0] text-[#2d3a1e] placeholder:text-[#b5a890] focus:border-[#6b7f4e] focus:ring-[#6b7f4e]/20 pr-10 h-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b5a890] hover:text-[#6b7f4e] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#6b7f4e] hover:bg-[#b89555] text-white font-semibold h-11 text-base shadow-md"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("auth.anmelden")}
                </Button>
                <div className="flex justify-between text-sm pt-1">
                  <button type="button" onClick={() => { setMode("forgot"); setMessage(null); }} className="text-[#5a7247] hover:text-[#3d5030] font-medium transition-colors">
                    {t("auth.passwortVergessen")}
                  </button>
                  <button type="button" onClick={() => { setMode("register"); setMessage(null); }} className="text-[#6b7f4e] hover:text-[#b89555] font-medium transition-colors underline underline-offset-2">
                    {t("auth.nochKeinKonto")}
                  </button>
                </div>
              </form>
            )}

            {/* Register Form */}
            {mode === "register" && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-[#2d3a1e] font-medium text-sm">{t("auth.vorname")}</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder={t("auth.vorname")}
                      required
                      className="bg-[#f9f6f0] border-[#d4c5a0] text-[#2d3a1e] placeholder:text-[#b5a890] focus:border-[#6b7f4e] focus:ring-[#6b7f4e]/20 h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-[#2d3a1e] font-medium text-sm">{t("auth.nachname")}</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder={t("auth.nachname")}
                      required
                      className="bg-[#f9f6f0] border-[#d4c5a0] text-[#2d3a1e] placeholder:text-[#b5a890] focus:border-[#6b7f4e] focus:ring-[#6b7f4e]/20 h-11"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="regEmail" className="text-[#2d3a1e] font-medium text-sm">{t("auth.email")}</Label>
                  <Input
                    id="regEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.platzhalterEmail")}
                    required
                    className="bg-[#f9f6f0] border-[#d4c5a0] text-[#2d3a1e] placeholder:text-[#b5a890] focus:border-[#6b7f4e] focus:ring-[#6b7f4e]/20 h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="regPassword" className="text-[#2d3a1e] font-medium text-sm">{t("auth.passwort")}</Label>
                  <div className="relative">
                    <Input
                      id="regPassword"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("auth.passwortMinLength")}
                      required
                      minLength={8}
                      className="bg-[#f9f6f0] border-[#d4c5a0] text-[#2d3a1e] placeholder:text-[#b5a890] focus:border-[#6b7f4e] focus:ring-[#6b7f4e]/20 pr-10 h-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b5a890] hover:text-[#6b7f4e] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#6b7f4e] hover:bg-[#b89555] text-white font-semibold h-11 text-base shadow-md"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("auth.kontoErstellen")}
                </Button>
                <div className="text-center pt-1">
                  <button type="button" onClick={() => { setMode("login"); setMessage(null); }} className="text-sm text-[#5a7247] hover:text-[#3d5030] font-medium transition-colors">
                    {t("auth.bereitsKonto")}
                  </button>
                </div>
              </form>
            )}

            {/* Forgot Password Form */}
            {mode === "forgot" && (
              <form onSubmit={handleForgot} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgotEmail" className="text-[#2d3a1e] font-medium text-sm">{t("auth.email")}</Label>
                  <Input
                    id="forgotEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.platzhalterEmail")}
                    required
                    className="bg-[#f9f6f0] border-[#d4c5a0] text-[#2d3a1e] placeholder:text-[#b5a890] focus:border-[#6b7f4e] focus:ring-[#6b7f4e]/20 h-11"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#6b7f4e] hover:bg-[#b89555] text-white font-semibold h-11 text-base shadow-md"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("auth.linkSenden")}
                </Button>
                <div className="text-center pt-1">
                  <button type="button" onClick={() => { setMode("login"); setMessage(null); }} className="text-sm text-[#5a7247] hover:text-[#3d5030] font-medium transition-colors">
                    {t("auth.zurueckZumLogin")}
                  </button>
                </div>
              </form>
            )}

            {/* Reset Password Form (via token from email) */}
            {mode === "reset" && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-[#2d3a1e] font-medium text-sm">{t("auth.neuesPasswortLabel")}</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t("auth.passwortMinLength")}
                      required
                      minLength={8}
                      className="bg-[#f9f6f0] border-[#d4c5a0] text-[#2d3a1e] placeholder:text-[#b5a890] focus:border-[#6b7f4e] focus:ring-[#6b7f4e]/20 pr-10 h-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b5a890] hover:text-[#6b7f4e] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#6b7f4e] hover:bg-[#b89555] text-white font-semibold h-11 text-base shadow-md"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("auth.passwortAendern")}
                </Button>
                <div className="text-center pt-1">
                  <button type="button" onClick={() => { setMode("login"); setMessage(null); setResetToken(""); window.history.replaceState({}, "", window.location.pathname); }} className="text-sm text-[#5a7247] hover:text-[#3d5030] font-medium transition-colors">
                    {t("auth.zurueckZumLogin")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* ═══════ WAS DICH NACH DER ANMELDUNG ERWARTET ═══════ */}
        {showOnboardingInfo && (
          <>
            {/* Feature Preview Section */}
            <div className="mt-8">
              <p className="text-center text-xs font-semibold tracking-widest uppercase text-[#8a7d65] mb-4">
                {t("auth.wasErwartet")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: MessageCircle, title: t("auth.annamTitle"), desc: t("auth.annamDesc") },
                  { icon: Mic, title: t("auth.sprachCoach"), desc: t("auth.sprachCoachDesc") },
                  { icon: BookOpen, title: t("auth.tagebuchFeature"), desc: t("auth.tagebuchFeatureDesc") },
                  { icon: Users, title: t("auth.communityFeature"), desc: t("auth.communityFeatureDesc") },
                ].map(({ icon: Icon, title, desc }) => (
                  <div
                    key={title}
                    className="bg-white/80 border border-[#d4c5a0]/40 rounded-xl p-4 text-center hover:shadow-md transition-shadow"
                  >
                    <Icon className="w-7 h-7 mx-auto mb-2 text-[#6b7f4e]" />
                    <h3 className="text-sm font-bold text-[#2d3a1e] mb-1">{title}</h3>
                    <p className="text-[11px] text-[#7d7060] leading-snug">{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Steps Section */}
            <div className="mt-6 bg-white/60 border border-[#d4c5a0]/30 rounded-xl p-5">
              <p className="text-center text-sm font-semibold text-[#6b7f4e] mb-4">
                {t("auth.soGehtEs")}
              </p>
              <div className="space-y-3">
                {[
                  { num: "1", text: t("auth.schritt1") },
                  { num: "2", text: t("auth.schritt2") },
                  { num: "3", text: t("auth.schritt3") },
                ].map(({ num, text }) => (
                  <div key={num} className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-[#6b7f4e] text-white text-xs font-bold flex items-center justify-center shrink-0">
                      {num}
                    </span>
                    <span className="text-sm text-[#2d3a1e]">{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Footer text */}
        <p className="text-center text-xs text-[#b5a890] mt-6 flex items-center justify-center gap-1.5">
          <Shield className="w-3.5 h-3.5" />
          {t("auth.datensicherheit")}
        </p>
      </div>
    </div>
  );
}
